/**
 * Cómo se cuenta el juego en voz.
 *
 * Este módulo es la accesibilidad del juego. Un vidente lee el panel de un
 * vistazo; quien juega escuchando recibe una frase, y si esa frase es larga,
 * confusa o llena de guiones bajos, el juego se vuelve injugable. Todo lo que se
 * dice sale de aquí, sin DOM, para poder probarlo entero.
 *
 * Tres decisiones que gobiernan el resto:
 *
 * 1. **Los huecos se agrupan**: «dos huecos, A, hueco» y no «hueco hueco A
 *    hueco». Un panel de veinte letras leído casilla a casilla no se sigue.
 * 2. **Los signos no se leen** en el modo normal. Están a la vista desde el
 *    principio y no hay nada que adivinar en ellos: nombrarlos solo alarga.
 *    Quien los quiera, tiene el modo casilla a casilla.
 * 3. **Primero lo nuevo, después el panel entero.** Al descubrirse una letra
 *    importa dónde ha caído; releer todo sin más obliga a recomponerlo de memoria.
 */

import type { GameEvent, PanelVerbosity, PlayerView, RoundView } from '../shared/protocol.js';
import type { Letter } from '../shared/letters.js';
import type { LetterPosition, PanelView } from '../shared/panel.js';

/** Números en palabras, que se oyen mejor que las cifras en mitad de una frase. */
const NUMBERS = [
  'cero', 'una', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
  'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete',
  'dieciocho', 'diecinueve', 'veinte',
];

/**
 * @brief Escribe un número con letra cuando es pequeño.
 * @param value Número a decir.
 * @return La palabra, o la cifra si se sale de la tabla.
 */
export function numberWord(value: number): string {
  return NUMBERS[value] ?? String(value);
}

/** @brief «una letra» / «cinco letras», concordando. */
function letterCount(count: number): string {
  return count === 1 ? 'una letra' : `${numberWord(count)} letras`;
}

/**
 * @brief Lee una palabra del panel.
 *
 * @param cells Casillas de la palabra: el carácter si está descubierto, cadena
 *        vacía si sigue oculto.
 * @param index Posición de la palabra en el panel, empezando en 0.
 * @param verbosity Agrupada (lo normal) o casilla a casilla.
 * @return La palabra dicha en voz.
 */
export function describeWord(cells: readonly string[], index: number, verbosity: PanelVerbosity): string {
  const label = `Palabra ${index + 1}`;
  if (verbosity === 'cell') {
    // La misma frase que en la lista navegable: el panel hablado y el explorado
    // no pueden decir cosas distintas de la misma casilla.
    const parts = cells.map((cell, position) => describeCell(cell, position));
    return `${label}: ${parts.join('. ')}.`;
  }

  // En modo agrupado los signos no se nombran, así que tampoco cuentan como
  // letras: «cinco letras» debe ser lo que de verdad hay que adivinar más lo ya
  // descubierto, no los adornos.
  const meaningful = cells.filter((cell) => cell === '' || isLetterCell(cell));
  const parts: string[] = [];
  let gap = 0;
  for (const cell of meaningful) {
    if (cell === '') {
      gap += 1;
      continue;
    }
    if (gap > 0) {
      parts.push(gap === 1 ? 'hueco' : `${numberWord(gap)} huecos`);
      gap = 0;
    }
    parts.push(cell);
  }
  if (gap > 0) parts.push(gap === 1 ? 'hueco' : `${numberWord(gap)} huecos`);
  return `${label}, ${letterCount(meaningful.length)}: ${parts.join(', ')}.`;
}

/** true si la casilla descubierta es una letra y no un signo de puntuación. */
function isLetterCell(cell: string): boolean {
  return /\p{Letter}/u.test(cell);
}

/**
 * @brief Lee una casilla suelta del panel.
 *
 * En modo explorar cada casilla es su propio elemento de lista, así que esta es
 * la frase entera que se oye al pararse en ella con las flechas: tiene que
 * bastarse sola, sin haber oído la palabra.
 *
 * @param cell El carácter si está descubierto, cadena vacía si sigue oculto.
 * @param position Posición dentro de su palabra, empezando en 0.
 * @return «posición 3, A» / «posición 4, hueco».
 */
export function describeCell(cell: string, position: number): string {
  return cell === '' ? `posición ${position + 1}, hueco` : `posición ${position + 1}, ${cell}`;
}

/**
 * @brief Cabecera de una palabra cuando el panel se recorre casilla a casilla.
 *
 * Ahí la palabra no se lee entera: es el elemento que agrupa a sus casillas y
 * solo dice cuál es y cuánto mide. El detalle vive en cada casilla, que es donde
 * ahora puede pararse el lector.
 *
 * Los signos no se cuentan, igual que en el modo agrupado: «cinco letras» es lo
 * que hay que adivinar más lo descubierto, no los adornos.
 *
 * @param cells Casillas de la palabra.
 * @param index Posición de la palabra en el panel, empezando en 0.
 * @return «Palabra 2, cinco letras».
 */
export function describeWordHeader(cells: readonly string[], index: number): string {
  const meaningful = cells.filter((cell) => cell === '' || isLetterCell(cell));
  return `Palabra ${index + 1}, ${letterCount(meaningful.length)}`;
}

/**
 * @brief Lee el panel: palabra por palabra y lo que falta.
 *
 * La cabecera (categoría y medida) se puede quitar porque el panel se relee
 * cada vez que se descubre una letra: repetir ahí «categoría, seis palabras,
 * diecinueve letras» en cada acierto alarga el aviso sin decir nada nuevo. Va
 * entera cuando el panel se pide a propósito.
 *
 * @param panel Panel tal y como lo manda el servidor.
 * @param verbosity Agrupada o casilla a casilla.
 * @param includeHeader true para empezar por la categoría y la medida.
 */
export function describePanel(panel: PanelView, verbosity: PanelVerbosity, includeHeader = true): string {
  const words = panel.words.length;
  const letters = panel.words.flat().filter((cell) => cell === '' || isLetterCell(cell)).length;
  const hidden = panel.words.flat().filter((cell) => cell === '').length;
  const head = includeHeader
    ? `Categoría: ${panel.category}. ${words === 1 ? 'Una palabra' : `${capitalize(numberWord(words))} palabras`}, ${letterCount(letters)}. `
    : '';
  const body = panel.words.map((cells, index) => describeWord(cells, index, verbosity)).join(' ');
  const tail =
    hidden === 0
      ? 'El panel está entero.'
      : `${hidden === 1 ? 'Queda' : 'Quedan'} ${letterCount(hidden)} por descubrir.`;
  return `${head}${body} ${tail}`;
}

/**
 * @brief Dice dónde ha aparecido una letra recién descubierta.
 *
 * Las posiciones se agrupan por palabra («palabra 1, posiciones 2 y 5») porque
 * es como se recuerda un panel de oído: por palabras, no por casillas sueltas.
 *
 * @param letter Letra pedida.
 * @param positions Dónde ha aparecido.
 * @return La frase con lo nuevo, o el aviso de que no estaba.
 */
export function describeReveal(letter: Letter, positions: readonly LetterPosition[]): string {
  if (positions.length === 0) return `No hay ninguna ${letter}.`;

  const byWord = new Map<number, number[]>();
  for (const position of positions) {
    const list = byWord.get(position.word) ?? [];
    list.push(position.cell + 1);
    byWord.set(position.word, list);
  }
  const chunks = [...byWord.entries()].map(([word, cells]) => {
    const which = cells.length === 1 ? `posición ${cells[0]}` : `posiciones ${joinList(cells.map(String))}`;
    return `palabra ${word + 1}, ${which}`;
  });
  const head =
    positions.length === 1 ? `¡Una ${letter}!` : `¡${capitalize(numberWord(positions.length))} letras ${letter}!`;
  return `${head} En ${joinList(chunks)}.`;
}

/** @brief Lista las letras ya pedidas en la ronda. */
export function describeUsedLetters(used: readonly Letter[]): string {
  if (used.length === 0) return 'Todavía no se ha pedido ninguna letra.';
  return `Letras pedidas: ${joinList([...used])}.`;
}

/**
 * @brief Dice cuánto lleva cada jugador.
 * @param players Jugadores de la sala.
 * @param meId Id de quien escucha, para decir «tú» en vez de su nombre.
 */
export function describeMoney(players: readonly PlayerView[], meId: string | null): string {
  const parts = players.map((player) => {
    const who = player.id === meId ? 'Tú' : player.name;
    const extras: string[] = [];
    if (player.wildcard) extras.push('con comodín');
    if (player.surprise) extras.push('con sobre');
    if (player.doubler) extras.push('con el por dos');
    if (player.jackpot) extras.push('en juego por el bote');
    const tail = extras.length > 0 ? `, ${joinList(extras)}` : '';
    return `${who}: ${money(player.bank)} en el banco, ${money(player.pot)} en esta ronda${tail}`;
  });
  return `${parts.join('. ')}.`;
}

/** @brief Dice de quién es el turno y qué se espera de él. */
export function describeTurn(round: RoundView, players: readonly PlayerView[], meId: string | null): string {
  const name = nameOf(round.turnPlayerId, players);
  const mine = round.turnPlayerId === meId;
  const subject = mine ? 'Te toca' : `Le toca a ${name}`;
  switch (round.turn.kind) {
    case 'consonant':
      return `${subject}: pedir una consonante. Cada una vale ${money(round.turn.value)}.`;
    case 'freeVowel':
      return `${subject}: elegir una vocal gratis.`;
    case 'penalty':
      return `${subject}: decidir si gasta el comodín para librarse de ${round.turn.label}.`;
    case 'express':
      return `${subject}: seguir en el exprés a ${money(round.turn.value)} por letra, o plantarse.`;
    case 'spinning':
      return `${subject}: parar la rueda. Si no la para, se para sola.`;
    case 'finalLetters': {
      const pending: string[] = [];
      if (round.turn.consonants > 0) {
        pending.push(
          round.turn.consonants === 1 ? 'una consonante' : `${numberWord(round.turn.consonants)} consonantes`,
        );
      }
      if (round.turn.vowels > 0) pending.push('una vocal');
      return `${subject}: elegir ${joinList(pending)}. No se descubren hasta tenerlas todas.`;
    }
    case 'finalSolve': {
      const left = round.turn.attemptsLeft;
      if (left === null) return `${subject}: decir la frase. ${describeTimeLeft(round.deadline)}`;
      return `${subject}: decir la frase. ${left === 1 ? 'Le queda un intento' : `Le quedan ${numberWord(left)} intentos`}.`;
    }
    case 'choosing':
    default:
      return `${subject}: tirar la rueda, comprar vocal o resolver.`;
  }
}

/**
 * @brief Convierte un evento del servidor en lo que se anuncia.
 *
 * @param event Evento recibido.
 * @param players Jugadores, para poner nombres.
 * @param meId Id de quien escucha.
 * @return El aviso, o null si ese evento no se anuncia por sí solo (hay eventos
 *         que solo sirven para refrescar la pantalla).
 */
export function describeEvent(
  event: GameEvent,
  players: readonly PlayerView[],
  meId: string | null,
  manualWheel = false,
): string | null {
  const who = (id: string): string => nameOf(id, players);
  switch (event.kind) {
    case 'playerJoined':
      return `${event.name} entra en la sala.`;
    case 'playerLeft':
      return `${event.name} se va de la sala.`;
    case 'gameStarted':
      return '¡Empieza la partida!';
    case 'roundStarted':
      return event.isFinal
        ? `Prueba final. Categoría: ${event.category}.`
        : `Ronda ${event.index}. Categoría: ${event.category}.`;
    case 'turnStarted':
      return event.playerId === meId ? 'Es tu turno.' : `Turno de ${who(event.playerId)}.`;
    case 'wheelStarted':
      return event.playerId === meId
        ? 'La rueda gira. Púlsala para pararla, o déjala y se parará sola.'
        : `${who(event.playerId)} lanza la rueda…`;
    case 'spun':
      // Parándola a mano ya se lanzó antes: aquí lo que pasa es que se para.
      return manualWheel
        ? `La rueda de ${who(event.playerId)} para en… ¡${event.label}!`
        : `${who(event.playerId)} tira la rueda… ¡${event.label}!`;
    case 'letterAsked':
      if (event.count === 0) return `${who(event.playerId)} pide la ${event.letter}. No hay ninguna.`;
      return `${who(event.playerId)} pide la ${event.letter}. ${describeReveal(event.letter, event.positions)} ${money(event.earned)} a la bolsa.`;
    case 'letterRepeated':
      return `La ${event.letter} ya se había pedido.`;
    case 'vowelTaken': {
      const action =
        event.price === 0
          ? `${who(event.playerId)} coge la ${event.letter} de la vocal gratis.`
          : `${who(event.playerId)} compra la ${event.letter} por ${money(event.price)}.`;
      if (event.count === 0) return `${action} No hay ninguna ${event.letter}.`;
      return `${action} ${describeReveal(event.letter, event.positions)}`;
    }
    case 'bankrupt':
      // Sin repetir «¡Quiebra!»: ya lo ha dicho el aviso de la tirada, y oírlo
      // dos veces seguidas suena a error del juego.
      return event.lost > 0
        ? `${who(event.playerId)} pierde ${money(event.lost)} y todo lo que llevaba encima.`
        : `${who(event.playerId)} no llevaba nada en la bolsa.`;
    case 'turnLost':
      return turnLostText(event.reason);
    case 'wildcardGained':
      return `${who(event.playerId)} se lleva el comodín.`;
    case 'wildcardSpent':
      return event.use === 'save'
        ? `${who(event.playerId)} gasta el comodín y se libra.`
        : `${who(event.playerId)} gasta el comodín para pedir una consonante extra.`;
    case 'doublerGained':
      return `${who(event.playerId)} se lleva el por dos: su próxima cantidad vale el doble.`;
    case 'doublerUsed':
      return `¡Por dos! Cada letra vale ${money(event.value)}.`;
    case 'surpriseGained':
      return `${who(event.playerId)} se lleva un sobre sorpresa. Se abrirá si resuelve el panel.`;
    case 'jackpotEntered':
      return `${who(event.playerId)} entra en juego por el bote: ${money(event.jackpot)}.`;
    case 'expressStarted':
      // Sin repetir «¡Exprés!»: ya lo ha dicho el aviso de la tirada.
      return `${who(event.playerId)} puede encadenar consonantes a ${money(event.value)} sin volver a tirar, pero si falla una, quiebra.`;
    case 'expressStopped':
      return `${who(event.playerId)} se planta.`;
    case 'nothingHappened':
      return event.reason;
    case 'finalStarted':
      return `¡Prueba final! La juega ${who(event.playerId)}, que es quien más dinero lleva, y se juega el bote: ${money(event.prize)}. De regalo: ${joinList([...event.freeLetters])}.`;
    case 'finalLetterPicked': {
      const pending: string[] = [];
      if (event.consonantsLeft > 0) {
        pending.push(
          event.consonantsLeft === 1 ? 'una consonante' : `${numberWord(event.consonantsLeft)} consonantes`,
        );
      }
      if (event.vowelsLeft > 0) pending.push('una vocal');
      const tail = pending.length > 0 ? ` Le queda${pending.length > 1 ? 'n' : ''} ${joinList(pending)}.` : '';
      return `${who(event.playerId)} elige la ${event.letter}.${tail}`;
    }
    case 'finalRevealed': {
      if (event.found.length === 0) return `Se descubren las letras… y no había ninguna.`;
      return `Se descubren las letras… ¡${joinList([...event.found])}!`;
    }
    case 'finalTimeUp':
      return '¡Se acabó el tiempo!';
    case 'solutionFailed':
      // El «no era» lo pone el aviso de cambio de turno que viene detrás.
      return `${who(event.playerId)} dice «${event.text}».`;
    case 'solved':
      return solvedText(who(event.playerId), event);
    case 'roundEnded':
      return `La frase era: ${event.phrase}.`;
    case 'gameOver':
      return event.winnerId === null
        ? 'Fin de la partida. Hay empate.'
        : `Fin de la partida. Gana ${who(event.winnerId)}.`;
    default:
      return null;
  }
}

/** Texto del panel resuelto, con el bote y el sobre si los hubo. */
function solvedText(name: string, event: Extract<GameEvent, { kind: 'solved' }>): string {
  const extras: string[] = [];
  if (event.jackpot > 0) extras.push(`el bote de ${money(event.jackpot)}`);
  if (event.surprise) extras.push(`el sobre sorpresa: ${event.surprise}`);
  const tail = extras.length > 0 ? `, incluido ${joinList(extras)}` : '';
  return `¡${name} resuelve el panel! Se lleva ${money(event.won)}${tail}.`;
}

/** Por qué se pierde el turno, dicho de forma que se entienda sin contexto. */
function turnLostText(reason: Extract<GameEvent, { kind: 'turnLost' }>['reason']): string {
  switch (reason) {
    case 'missingLetter':
      return 'Esa consonante no estaba: cambia el turno.';
    case 'missingVowel':
      return 'Esa vocal no estaba: cambia el turno.';
    case 'wrongSolution':
      return 'No era: cambia el turno.';
    case 'wedge':
    default:
      return 'Cambia el turno.';
  }
}

/** @brief Escribe una cantidad como se dice. */
export function money(amount: number): string {
  return `${amount} euros`;
}

/**
 * @brief Dice cuánto tiempo queda, redondeado a lo que se puede seguir de oído.
 *
 * Se calcula **al pedirlo**, no se refresca solo: un reloj que se reescribe en
 * una región viva deja al lector de pantalla hablando sin parar y tapa el juego.
 * Por eso también se redondea: al segundo exacto no aporta nada y suena a
 * cuenta atrás de bomba.
 *
 * @param deadline Instante (epoch ms) en que se acaba, o null si no hay reloj.
 */
export function describeTimeLeft(deadline: number | null): string {
  if (deadline === null) return 'No hay reloj: sin prisa.';
  const seconds = Math.max(0, Math.round((deadline - Date.now()) / 1000));
  if (seconds === 0) return 'Se acabó el tiempo.';
  if (seconds === 1) return 'Queda un segundo.';
  return `Quedan ${numberWord(seconds)} segundos.`;
}

/**
 * Nombre de un jugador.
 *
 * Se usa el nombre también para quien escucha, en vez de «tú»: los avisos son
 * frases en tercera persona («Ana pide la S») y meter ahí un «tú» las deja mal
 * dichas. El «te toca» lo llevan los avisos de turno, que sí están escritos para
 * eso.
 */
function nameOf(id: string, players: readonly PlayerView[]): string {
  return players.find((player) => player.id === id)?.name ?? 'alguien';
}

/** Primera letra en mayúscula, para empezar frase. */
function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Une una lista como se dice hablando: «a, b y c». */
function joinList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}
